// ─────────────────────────────────────────────────────────────────────────────
//  LANAC — chargeur i18n (Phase 1 lazy-load, 2026-07-18)
//
//  Les dictionnaires vivent en JSON par-locale (src/i18n/<loc>.json, émis hashés
//  dans dist/i18n/ par build.mjs). Ce module les charge au boot et PEUPLE les
//  coquilles T[loc] de 04-i18n-dict.jsx par MUTATION (le binding const reste,
//  on remplit l'objet qu'il pointe — même principe que setActiveLang).
//
//  SÉCURITÉ (aucun compromis) :
//   • Locale VALIDÉE (regex + allowlist du manifeste) AVANT de construire l'URL
//     → aucun fetch forgeable, aucun path traversal.
//   • Manifeste INJECTÉ AU BUILD par `define` esbuild (le nom du define : voir la constante ci-dessous — jamais écrit en toutes lettres dans un
//     commentaire : une construction NON minifiée garde les commentaires, et la garde « define non substitué » de build.mjs le prendrait pour
//     un oubli — le serveur de développement ne se construisait plus ; lot D · D5-c, 30/10/2026)
//     → il fait partie du bundle hashé : pas de requête séparée, pas de skew
//     app/packs, sourcemap exact (substitution AVANT minification).
//   • Intégrité : SHA-256 du pack vérifié (Web Crypto) contre le manifeste avant
//     application. Un pack altéré (CDN/poisoning) = rejeté = repli.
//   • Donnée, pas code : JSON.parse strict. Jamais eval/Function/script.
//   • credentials:"omit" — un pack est un asset public, aucun cookie ne part.
//
//  DISPONIBILITÉ (le boot ne doit JAMAIS briquer l'app) :
//   • Le rendu attend `active` + `fr` (repli) — les AUTRES locales se chargent
//     en parallèle sans bloquer (nécessaires à la reverse-map des notifications
//     multilingues, cf. lanacFillNotifReverse — retiré en Phase 3).
//   • Garde-fou 8 s : si le réseau pend, on rend quand même (tx() retombe sur
//     fr ou la clé) — une UI dégradée vivante vaut mieux qu'un splash infini.
//   • lanacBootLocale() = la locale GARANTIE chargée au moment du rendu (active
//     si OK, sinon fr). Les ~125 accès directs `T[lang].x` ne rencontrent ainsi
//     JAMAIS une coquille vide (un objet vide est truthy → `T[lang]||T.fr` ne
//     protégerait pas).
// ─────────────────────────────────────────────────────────────────────────────
import { T, setActiveLang, lanacFillNotifReverse } from "../04-i18n-dict.jsx";

// Injecté par build.mjs via esbuild `define`. En import Node nu (tests/outils),
// l'identifiant n'existe pas → null → module inerte (aucun fetch hors navigateur).
const I18N_MANIFEST = typeof __LANAC_I18N_MANIFEST__ !== "undefined" ? __LANAC_I18N_MANIFEST__ : null;

const LOCALE_RE = /^[a-z]{2}(-[A-Z]{2})?$/;
const FALLBACK_LOCALE = "fr";
// Allowlist runtime = clés du manifeste (elles-mêmes re-validées par la regex).
// Sans manifeste (Node), la liste historique — les composants peuvent toujours lister.
const SUPPORTED_LOCALES = I18N_MANIFEST
  ? Object.keys(I18N_MANIFEST).filter((l) => LOCALE_RE.test(l))
  : ["fr", "en", "de", "pt"];

const loadedLocales = new Set();
const packCache = {};          // loc -> pack.T (source de la reverse-map notifs)
const inflight = {};           // loc -> Promise (dédup des chargements concurrents)
let bootLocale = FALLBACK_LOCALE; // locale garantie chargée au moment du rendu

// ⭐ CORRECTIF crash boot (19/07) : le pack de REPLI fr est BUNDLÉ (import dans
//   04-i18n-dict → T.fr toujours peuplé). On le déclare chargé D'EMBLÉE et on ne
//   le fetch JAMAIS : le repli est ainsi INFAILLIBLE quoi qu'il arrive au réseau/
//   SW/déploiement (fini le crash « coquille vide » sur T[lang].nav.msg). Seules
//   en/de/pt restent en lazy-load réseau.
if (T && T[FALLBACK_LOCALE] && Object.keys(T[FALLBACK_LOCALE]).length) {
  loadedLocales.add(FALLBACK_LOCALE);
  packCache[FALLBACK_LOCALE] = T[FALLBACK_LOCALE];
}

const report = (msg, extra) => { try { window.__lncReport && window.__lncReport("i18n", msg, extra || {}); } catch (_) {} };

// Locale demandée par l'utilisateur (même clé localStorage qu'useUiPrefs), validée.
const resolveLocale = () => {
  let raw = null;
  try { raw = localStorage.getItem("lanac:lang"); } catch (_) {}
  return raw && LOCALE_RE.test(raw) && SUPPORTED_LOCALES.includes(raw) ? raw : FALLBACK_LOCALE;
};

const toHex = (buf) => Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("");

async function fetchPack(loc) {
  const entry = I18N_MANIFEST && I18N_MANIFEST[loc];
  if (!entry || !entry.url) throw new Error(`locale hors manifeste: ${loc}`);
  const res = await fetch(entry.url, { credentials: "omit" });
  if (!res.ok) throw new Error(`HTTP ${res.status} sur ${entry.url}`);
  const buf = await res.arrayBuffer();
  // Intégrité : SHA-256 contre le manifeste (silencieusement sauté si Web Crypto
  // absent — contexte non sécurisé n'arrive pas en prod https ni sur 127.0.0.1).
  if (entry.sha256 && typeof crypto !== "undefined" && crypto.subtle) {
    const digest = toHex(await crypto.subtle.digest("SHA-256", buf));
    if (digest !== entry.sha256) throw new Error(`intégrité: sha256 inattendu pour ${loc}`);
  }
  const pack = JSON.parse(new TextDecoder().decode(buf));
  if (!pack || typeof pack.T !== "object" || !pack.T) throw new Error(`pack invalide: ${loc}`);
  return pack;
}

function applyPack(loc, pack) {
  const target = T[loc] || (T[loc] = {});
  for (const k of Object.keys(target)) delete target[k]; // remplace le CONTENU, le binding reste
  Object.assign(target, pack.T);
  packCache[loc] = pack.T;
  loadedLocales.add(loc);
  // Reverse-map des notifications : dès que les 4 locales du protocole {fr,en,de,pt}
  // sont là (le compose-side a besoin de TOUTES — levé en Phase 3).
  lanacFillNotifReverse(packCache);
}

function lanacLoadLocale(loc) {
  if (!LOCALE_RE.test(loc) || !SUPPORTED_LOCALES.includes(loc)) return Promise.reject(new Error(`locale non supportée: ${loc}`));
  if (loadedLocales.has(loc)) return Promise.resolve(true);
  if (!inflight[loc]) {
    inflight[loc] = fetchPack(loc).then((pack) => { applyPack(loc, pack); delete inflight[loc]; return true; })
      .catch((e) => { delete inflight[loc]; throw e; });
  }
  return inflight[loc];
}

const lanacLocaleLoaded = (loc) => loadedLocales.has(loc);
const lanacBootLocale = () => bootLocale;

// ── Boot (navigateur seulement) : lance TOUT en parallèle, ne bloque le rendu
//   que sur active+fr, plafonné à 8 s. Ne rejette JAMAIS.
let readyPromise = Promise.resolve();
if (typeof window !== "undefined" && typeof fetch === "function" && I18N_MANIFEST) {
  const active = resolveLocale();
  // Locales non critiques : chargement de fond + UNE relance à 5 s (la reverse-map
  // notifs en dépend ; au-delà, dégradation douce identique à une chaîne inconnue).
  for (const l of SUPPORTED_LOCALES) {
    if (l === active || l === FALLBACK_LOCALE) continue;
    lanacLoadLocale(l).catch(() => setTimeout(() => { lanacLoadLocale(l).catch((e) => report("pack de fond indisponible", { locale: l, err: String(e && e.message) })); }, 5000));
  }
  const critical = Promise.allSettled([...new Set([active, FALLBACK_LOCALE])].map((l) => lanacLoadLocale(l)));
  const cap = new Promise((r) => setTimeout(r, 8000)); // garde-fou : jamais de splash infini
  readyPromise = Promise.race([critical, cap]).then(() => {
    bootLocale = loadedLocales.has(active) ? active : FALLBACK_LOCALE;
    if (bootLocale !== active) report("locale active indisponible au boot — repli fr", { locale: active });
    setActiveLang(bootLocale);
  });
}
const lanacI18nReady = readyPromise;

export { SUPPORTED_LOCALES, lanacI18nReady, lanacBootLocale, lanacLoadLocale, lanacLocaleLoaded };
